<template>
  <Modal
    sheetbelowsm
    close-button
    :title="$t('flow.copy_evaluate_items')"
    :primary-button-loading="loading"
    :primary-button-disabled="!(question || (conversation && step) || (project && flow))"
    :primary-button="$t('save')"
    :secondary-button="$t('cancel')"
    @primary="onSaveClick"
    @secondary="close"
    @close="close"
  >
    <div class="sm:w-600 px-10">
      <FormSelect v-model="project" :options="projectList" :label="$t('flow.projects')" appendToBody default-reducer class="mt-3" @update:modelValue="onProjectUpdated" />
      <FormSelect v-model="flowId" :options="flows" appendToBody :label="$t('projects.flows.flow')" class="mt-3" default-reducer @update:modelValue="onProjectFlowUpdated" />
      <FormSelect v-model="step" :options="flowSteps" appendToBody :label="$t('flow.step')" class="mt-3" default-reducer show-add-button @add="createStep" />
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex';
import uuidv4 from '@/helpers/uuid';
import { addNewStep } from '@/helpers/flow';

export default {
  name: 'EvaluateCopyModal',
  emits: ['close', 'addToCurrent'],
  props: {
    selectedItemIds: {
      type: Array,
      default: () => [],
    },
    evaluateData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      copyTo: null,
      conversation: null,
      question: null,
      step: null,
      project: null,
      flowId: null,
    };
  },
  computed: {
    ...mapState(['projects', 'projectData', 'selectedIntentDefinition', 'selectedIntentDefinitionStepId']),
    ...mapGetters(['getProjectFlows']),
    projectList() {
      return Object.keys(this.projects).map((key) => ({ label: this.projects[key].name, value: key })) || [];
    },
    flows() {
      if (!this.project) return [];
      const flows = this.getProjectFlows(this.project);
      return Object.keys(flows).map((flowId) => ({ label: flows[flowId].name, value: flowId }));
    },
    flow() {
      if (!this.project) return null;
      const flows = this.getProjectFlows(this.project);
      return flows[this.flowId];
    },
    flowSteps() {
      if (!this.project) return [];
      const steps = this.flow?.steps || [];
      return steps.map((step) => ({ label: step.name, value: step.id }));
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'updateProjectFlow', 'fetchProjectFlows', 'fetchProjectFlow']),
    ...mapMutations(['SET_SELECTED_INTENT_DEFINITION_STEP', 'ADD_CONVERSATION_STEP', 'ADD_SELECTED_INTENT_DEFINITION_STEP', 'ADD_PROJECT_STEP']),
    async createStep() {
      const steps = structuredClone(this.flow)?.steps || [];
      const stepItem = addNewStep(steps);
      const { flow_id, name, transition = '' } = structuredClone(this.flow);
      try {
        this.loading = true;
        const response = await this.updateProjectFlow({
          project_id: this.project,
          flow_id,
          name,
          transition,
          steps,
        });
        this.loading = false;
        if (response) {
          this.ADD_PROJECT_STEP({ projectId: this.project, flowId: flow_id, stepItem });
          if (this.selectedIntentDefinition && this.selectedIntentDefinition.endpoint_id === this.endpoint && this.$route.name === 'project-flow') {
            this.ADD_SELECTED_INTENT_DEFINITION_STEP(stepItem);
          }
          return;
        }
        this.showToastMessage({ title: this.$t('flow.failed_to_add_step'), type: 'error' });
      } catch (e) {
        this.loading = false;
        this.showToastMessage({ title: this.$t('flow.failed_to_add_step'), type: 'error' });
      }
    },
    close() {
      this.$emit('close');
    },
    async onSaveClick() {
      let items = this.evaluateData.filter((e) => this.selectedItemIds.includes(e.id));
      items = items.map((item) => ({ ...item, id: uuidv4() }));

      if (this.selectedIntentDefinitionStepId === this.step && this.$route.name === 'project-flow') {
        this.$emit('addToCurrent', items);
        this.close();
        return;
      }

      const steps = structuredClone(this.flow?.steps || []);
      const step = steps.find((s) => s.id === this.step);
      if (!Array.isArray(step.evaluate)) {
        step.evaluate = [];
      }
      step.evaluate = [...step.evaluate, ...items];

      const { flow_id, name, transition } = this.flow;
      try {
        this.loading = true;
        const response = await this.updateProjectFlow({
          project_id: this.project,
          flow_id,
          name,
          transition,
          steps,
        });
        this.loading = false;
        if (response) {
          this.SET_SELECTED_INTENT_DEFINITION_STEP(step);
          this.showToastMessage({ message: this.$t('flow.items_copied'), type: 'success' });
          this.close();
          return;
        }
        this.showToastMessage({ title: this.$t('flow.failed_to_copy_items'), type: 'error' });
      } catch (e) {
        this.loading = false;
        this.showToastMessage({ title: this.$t('flow.failed_to_copy_items'), type: 'error' });
      }
    },
    async onProjectUpdated() {
      if (this.project) {
        this.flowId = null;
        this.step = null;

        await this.fetchProjectFlows({ projectId: this.project });
      }
    },
    async onProjectFlowUpdated() {
      if (this.project && this.flowId) {
        this.step = null;

        await this.fetchProjectFlow({ projectId: this.project, flowId: this.flowId, updateFlowsOnly: true });
      }
    },
  },
};
</script>
